// One thing on the calendar, whichever source it came from. Every source normalizes into this shape so
// the views never branch on where an item was read.

export type CalendarItemKind = 'meeting' | 'booking' | 'task' | 'routine';

export const CALENDAR_ITEM_KINDS: readonly CalendarItemKind[] = ['meeting', 'booking', 'task', 'routine'];

export interface CalendarPerson {
  name?: string;
  email?: string;
}

export interface CalendarItem {
  /** Unique across sources: `<kind>:<source id>[:<occurrence>]`. */
  key: string;
  kind: CalendarItemKind;
  title: string;
  /** Epoch ms. */
  start: number;
  /** Epoch ms. Equal to `start` for a point in time (a task's due moment, a routine's run). */
  end: number;
  allDay: boolean;
  /** A routine that runs every day. The day and week grids draw each run at its time; a month cell, which has no
   *  times, leaves it out rather than repeat it on every day. */
  repeatsDaily?: boolean;
  /** Which account or site the item came from, for the layer it belongs to and the source line. */
  sourceId?: string;
  sourceLabel?: string;
  /** A connected account's sub-calendar the meeting is on. */
  calendar?: {
    id: string;
    name: string;
    /** The account's own main calendar, as the provider marks it. */
    primary?: boolean;
  };
  /** Where the item came from, for the mark beside its title: a calendar provider, a site's logo. */
  source?: { provider?: string; logoUrl?: string };
  /** The colour the provider draws the item in (its own, else its calendar's), as hex. */
  color?: string;
  /** The owner's own answer to a meeting invitation. */
  rsvp?: 'accepted' | 'declined' | 'tentative' | 'pending';
  /** What Wix Bookings holds about the booking(s) on a session: who, paid, attended. */
  booking?: BookingDetails;
  /** Owner action pending — a task waiting on the owner, a booking waiting for approval. */
  needsYou?: boolean;
  /** Finished, cancelled or declined: drawn quieter. */
  settled?: boolean;
  status?: string;
  location?: string;
  people?: CalendarPerson[];
  /** Bookings staff. */
  staff?: string[];
  /** Bookings classes: places taken and total. */
  capacity?: { booked: number; total: number };
  videoLink?: string;
  /** The owner can drag it to new times: a timed meeting they organize on a calendar they can write. */
  movable?: boolean;
  workspaceId?: string;
  /** In-app route (task, routine) or an outside link (Wix dashboard, provider calendar). */
  href?: string;
  external?: boolean;
  /** The source entity's own ids, kept for the write phase (move, cancel, RSVP). */
  ref: Record<string, string | undefined>;
}

export interface BookingClient {
  name?: string;
  email?: string;
  phone?: string;
  /** Lower-cased Wix values: `confirmed`, `pending`, `waiting_list`, `canceled`, `declined`. */
  status?: string;
  /** `paid`, `not_paid`, `partially_paid`, `refunded`; absent when no payment applies. */
  payment?: string;
  /** `online` or `offline`. */
  paymentOption?: string;
  /** `attended` or `no_show`; absent until it is marked. */
  attendance?: string;
  /** Who made it: the `business` (the owner, in the dashboard) or the `customer`. */
  bookedBy?: string;
}

export interface BookingDetails {
  clients: BookingClient[];
}

/** A half-open window, epoch ms: `from <= t < to`. */
export interface CalendarRange {
  from: number;
  to: number;
}

/**
 * Whether an item is drawn as a bar across the days rather than a block on the hours: an all-day item,
 * or a timed one that lasts a day or more (a week of reserve duty entered as one long event), the way
 * Google draws it.
 */
export function drawsAsAllDay(item: Pick<CalendarItem, 'allDay' | 'start' | 'end'>): boolean {
  return item.allDay || item.end - item.start >= 86_400_000;
}

export function overlapsRange(item: Pick<CalendarItem, 'start' | 'end'>, range: CalendarRange): boolean {
  return item.start < range.to && Math.max(item.end, item.start + 1) > range.from;
}
